import { useState, useEffect, useCallback } from "react";
import { Award, User, Mail, Calendar, AtSign, Building2, Phone, MapPin, Save, Pencil, Shield, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import SessionsManager from "../components/SessionsManager";
import { authFetch } from "../services/authService";
import {
  validatePartitaIVA,
  validateCodiceFiscale,
  validateCAP,
  validateProvincia,
  validatePEC,
  validateSDICode
} from "../utils/fiscalValidators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile({ user: initialUser }) {
  const [user, setUser] = useState(initialUser);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    vat_number: "",
    fiscal_code: "",
    address: "",
    zip_code: "",
    city: "",
    province: "",
    phone: "",
    pec_email: "",
    sdi_code: "",
    parts_markup: 30
  });

  const validateField = useCallback((field, value) => {
    let result = { isValid: true, error: null };
    switch (field) {
      case 'vat_number':
        result = validatePartitaIVA(value);
        break;
      case 'fiscal_code':
        result = validateCodiceFiscale(value);
        break;
      case 'zip_code':
        result = validateCAP(value);
        break;
      case 'province':
        result = validateProvincia(value);
        break;
      case 'pec_email':
        result = validatePEC(value);
        break;
      case 'sdi_code':
        result = validateSDICode(value);
        break;
      default:
        break;
    }
    setFieldErrors(prev => {
      const next = { ...prev };
      if (result.isValid || !value) delete next[field];
      else next[field] = result.error;
      return next;
    });
    return result.isValid;
  }, []);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (['vat_number', 'fiscal_code', 'zip_code', 'province', 'pec_email', 'sdi_code'].includes(field)) {
      validateField(field, value);
    }
  };

  const hasErrors = Object.keys(fieldErrors).length > 0;

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        company_name: user.company_name || "",
        vat_number: user.vat_number || "",
        fiscal_code: user.fiscal_code || "",
        address: user.address || "",
        zip_code: user.zip_code || "",
        city: user.city || "",
        province: user.province || "",
        phone: user.phone || "",
        pec_email: user.pec_email || "",
        sdi_code: user.sdi_code || "",
        parts_markup: user.parts_markup || user.profile?.parts_markup || 30
      });
      setFieldErrors({});
    }
  }, [user]);

  const handleSave = async () => {
    // Validate all fiscal fields before saving
    const fieldsToValidate = ['vat_number', 'fiscal_code', 'zip_code', 'province', 'pec_email', 'sdi_code'];
    let allValid = true;
    fieldsToValidate.forEach(field => {
      if (formData[field] && !validateField(field, formData[field])) {
        allValid = false;
      }
    });
    if (!allValid) {
      toast.error("Correggere i campi evidenziati prima di salvare");
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setEditing(false);
        toast.success("Profilo aggiornato con successo");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Profilo</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSessions(true)} className="border-zinc-700">
            <Shield className="w-5 h-5 mr-2" />
            Sessioni Attive
          </Button>
          {!editing ? (
            <Button onClick={() => setEditing(true)} className="btn-primary">
              <Pencil className="w-5 h-5 mr-2" />
              Modifica
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} className="border-zinc-700">
                Annulla
              </Button>
              <Button onClick={handleSave} className="btn-primary" disabled={loading || hasErrors}>
                <Save className="w-5 h-5 mr-2" />
                {loading ? "Salvataggio..." : "Salva"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sessions Manager Dialog */}
      <SessionsManager isOpen={showSessions} onClose={() => setShowSessions(false)} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Info */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Dati Account
            </h3>
            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 rounded-sm bg-primary/10 flex items-center justify-center border-2 border-primary">
                <span className="text-3xl font-bold text-primary font-['Barlow_Condensed']">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </span>
              </div>
              <div>
                {editing ? (
                  <input
                    type="text"
                    className="form-input text-xl font-bold"
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                  />
                ) : (
                  <h2 className="text-xl font-bold text-white">{user?.name}</h2>
                )}
                <p className="text-zinc-500">@{user?.username}</p>
                <span className="inline-block mt-2 px-3 py-1 bg-zinc-800 rounded-sm text-xs uppercase text-zinc-400">
                  {user?.role === "admin" ? "Amministratore" : "Utente"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                <AtSign className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs uppercase text-zinc-500">Username</p>
                  <p className="text-white font-['JetBrains_Mono']">{user?.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs uppercase text-zinc-500">Email</p>
                  <p className="text-white">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Dati Aziendali
            </h3>
            
            {editing ? (
              <div className="space-y-4">
                {hasErrors && (
                  <div data-testid="profile-validation-banner" className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800/50 rounded-sm text-red-400 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>Correggere i campi evidenziati in rosso prima di salvare</span>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Ragione Sociale</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.company_name}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Partita IVA</label>
                    <input
                      type="text"
                      data-testid="profile-vat-number"
                      className={`form-input uppercase ${fieldErrors.vat_number ? 'border-red-500 ring-1 ring-red-500/30' : formData.vat_number && !fieldErrors.vat_number ? 'border-green-600/50' : ''}`}
                      value={formData.vat_number}
                      onChange={(e) => handleFieldChange('vat_number', e.target.value.toUpperCase())}
                    />
                    {fieldErrors.vat_number && (
                      <p data-testid="vat-error" className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.vat_number}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale</label>
                    <input
                      type="text"
                      data-testid="profile-fiscal-code"
                      className={`form-input uppercase ${fieldErrors.fiscal_code ? 'border-red-500 ring-1 ring-red-500/30' : formData.fiscal_code && !fieldErrors.fiscal_code ? 'border-green-600/50' : ''}`}
                      value={formData.fiscal_code}
                      onChange={(e) => handleFieldChange('fiscal_code', e.target.value.toUpperCase())}
                    />
                    {fieldErrors.fiscal_code && (
                      <p data-testid="cf-error" className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.fiscal_code}
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Indirizzo</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">CAP</label>
                    <input
                      type="text"
                      data-testid="profile-zip-code"
                      className={`form-input ${fieldErrors.zip_code ? 'border-red-500 ring-1 ring-red-500/30' : formData.zip_code && !fieldErrors.zip_code ? 'border-green-600/50' : ''}`}
                      value={formData.zip_code}
                      onChange={(e) => handleFieldChange('zip_code', e.target.value)}
                    />
                    {fieldErrors.zip_code && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.zip_code}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Citta</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.city}
                      onChange={(e) => handleFieldChange('city', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Provincia</label>
                    <input
                      type="text"
                      data-testid="profile-province"
                      className={`form-input uppercase ${fieldErrors.province ? 'border-red-500 ring-1 ring-red-500/30' : formData.province && !fieldErrors.province ? 'border-green-600/50' : ''}`}
                      maxLength={2}
                      value={formData.province}
                      onChange={(e) => handleFieldChange('province', e.target.value.toUpperCase())}
                    />
                    {fieldErrors.province && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.province}
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">PEC</label>
                    <input
                      type="email"
                      data-testid="profile-pec"
                      className={`form-input ${fieldErrors.pec_email ? 'border-red-500 ring-1 ring-red-500/30' : formData.pec_email && !fieldErrors.pec_email ? 'border-green-600/50' : ''}`}
                      value={formData.pec_email}
                      onChange={(e) => handleFieldChange('pec_email', e.target.value)}
                    />
                    {fieldErrors.pec_email && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.pec_email}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice SDI</label>
                    <input
                      type="text"
                      data-testid="profile-sdi-code"
                      className={`form-input uppercase ${fieldErrors.sdi_code ? 'border-red-500 ring-1 ring-red-500/30' : formData.sdi_code && !fieldErrors.sdi_code ? 'border-green-600/50' : ''}`}
                      maxLength={7}
                      value={formData.sdi_code}
                      onChange={(e) => handleFieldChange('sdi_code', e.target.value.toUpperCase())}
                    />
                    {fieldErrors.sdi_code && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />{fieldErrors.sdi_code}
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Ricarico Ricambi (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-input w-32"
                    value={formData.parts_markup}
                    onChange={(e) => handleFieldChange('parts_markup', parseInt(e.target.value) || 0)}
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Ricarico applicato automaticamente ai prezzi dei ricambi dai fornitori
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <p className="text-xs uppercase text-zinc-500">Ragione Sociale</p>
                  <p className="text-white font-medium">{user?.company_name || "-"}</p>
                </div>
                <div className="p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <p className="text-xs uppercase text-zinc-500">Partita IVA</p>
                  <p className="text-white font-['JetBrains_Mono']">{user?.vat_number || "-"}</p>
                </div>
                <div className="p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <p className="text-xs uppercase text-zinc-500">Codice Fiscale</p>
                  <p className="text-white font-['JetBrains_Mono']">{user?.fiscal_code || "-"}</p>
                </div>
                <div className="col-span-2 p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs uppercase text-zinc-500">Indirizzo</p>
                      <p className="text-white">
                        {user?.address ? `${user.address}, ${user.zip_code} ${user.city} (${user.province})` : "-"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs uppercase text-zinc-500">Telefono</p>
                      <p className="text-white font-['JetBrains_Mono']">{user?.phone || "-"}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <p className="text-xs uppercase text-zinc-500">PEC</p>
                  <p className="text-white">{user?.pec_email || "-"}</p>
                </div>
                <div className="p-3 bg-zinc-950 rounded-sm border border-zinc-800">
                  <p className="text-xs uppercase text-zinc-500">Codice SDI</p>
                  <p className="text-white font-['JetBrains_Mono']">{user?.sdi_code || "-"}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Points Card */}
        <div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase text-zinc-500 tracking-wider">I Tuoi Punti</p>
                <p className="text-4xl font-bold text-white font-['JetBrains_Mono']">
                  {user?.points || 0}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm uppercase text-zinc-500 font-bold tracking-wider mb-2">
                  Come guadagnare punti
                </h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    Crea preventivi con articoli di marche sponsorizzate
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    Più articoli della stessa marca = più punti
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    I punti vengono assegnati automaticamente
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-600">
                  I punti possono essere convertiti in premi e sconti. Contatta l'amministratore per maggiori informazioni.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
